import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { color } from '../../app/theme';
import {
  GATriggerType,
  ProviderActionType,
  ProviderType,
  TriggerType,
  UrlFilterType,
} from '../../app/types';
import { changeSeconds } from '../../app/utils';
import Tooltip from '../shared/Tooltip';

const useStyles = makeStyles(
  {
    root: {
      fontSize: '17px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$3,
      paddingBottom: 24,
      textAlign: 'center',
    },
    gaView: {
      fontWeight: '400',
      fontSize: '13px',
      lineHeight: '18px',
      color: color.$11,
      display: 'inline-flex',
      alignItems: 'center',
    },
    url: {
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: '1',
      display: '-webkit-box',
      overflow: 'hidden',
      wordBreak: 'break-all',
      color: color.$1,
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    urlText: {
      color: color.$13,
      textDecoration: 'none',
      cursor: 'default',
    },
    urlType: {
      display: 'inline-flex',
      wordBreak: 'keep-all',
    },
    subtitle: {
      fontSize: '13px',
      lineHeight: '18.2px',
      color: color.$11,
      marginTop: 8,
    },
  },
  {
    name: 'PreviewTrigger',
  },
);

const PreviewTrigger = ({ node }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { data } = node || {};
  const { type, conditions, action } = data || {};
  const { url, staySec, urlType } = conditions?.[0] || {};

  const staySecText = useMemo(() => {
    const minute = changeSeconds(staySec, 'minute');
    const second = changeSeconds(staySec, 'second');

    if (staySec === null || (!second && !minute)) {
      return '';
    }

    return ` ${minute ? `${t('message-node.minute', { minute })}` : ''}${
      second ? ` ${t('message-node.second', { second })}` : ''
    }`;
  }, [staySec, t]);

  const content = useMemo(() => {
    switch (type) {
      case TriggerType.BIRTHDAY:
        return (
          <div>
            {t(`preview.trigger.${type}`, {
              conditions: t(`birthday.${conditions}`),
            })}
          </div>
        );
      case ProviderType.SHOPLINE:
      case ProviderType.SHOPIFY:
      case ProviderType.CYBERBIZ:
        switch (action) {
          case ProviderActionType.BINDEC:
            return (
              <div>
                {t(`preview.trigger.${type}`, {
                  conditions: t(`ec.${action}`),
                })}
              </div>
            );
          case ProviderActionType.MEMBERSHIPTIER:
            if (type === ProviderType.CYBERBIZ) {
              return (
                <div>
                  {t(`ec.membershipTierPreview`, {
                    type: t(`ec.${conditions?.type}`),
                    name: `${conditions?.groupName || ''}-${
                      conditions?.name || ''
                    }`,
                  })}
                </div>
              );
            }
            return (
              <div>
                {t(`ec.membershipTierPreview`, {
                  type: t(`ec.${conditions?.type}`),
                  name: conditions?.name || '',
                })}
              </div>
            );
          case ProviderActionType.TOTALORDERCOUNT:
          case ProviderActionType.TOTALORDERAMOUNT:
          case ProviderActionType.SINGLEORDERAMOUNT:
            return (
              <>
                <div>
                  {t(`preview.trigger.${type}`, {
                    conditions: t(`ec.${action}`),
                  })}
                </div>
                <div className={classes.subtitle}>{`${t(
                  `ec.${action}Conditions3`,
                )}${conditions} ${t(`ec.${action}Conditions2`)}`}</div>
              </>
            );
          case ProviderActionType.USERCREDIT:
          case ProviderActionType.MEMBERPOINT:
            return (
              <div>
                {t(`preview.trigger.${type}`, {
                  conditions: t(`ec.${action}Send`),
                })}
              </div>
            );
          default:
            return null;
        }
      case GATriggerType.GAVIEW:
        return (
          <>
            <div>{t(`preview.trigger.${type}`)}</div>
            {type === GATriggerType.GAVIEW && (
              <div style={{ marginTop: 8, textAlign: 'center' }}>
                {url && (
                  <div className={classes.gaView}>
                    <div className={classes.urlType}>{`${t(
                      `GA.${urlType}`,
                    )}`}</div>
                    ：
                    {UrlFilterType.EQ === urlType ? (
                      <a
                        className={classes.url}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {url}
                      </a>
                    ) : (
                      <Tooltip light placement="top" title={url}>
                        <span
                          className={classnames(classes.url, classes.urlText)}
                        >
                          {url}
                        </span>
                      </Tooltip>
                    )}
                  </div>
                )}
                {staySec && (
                  <div className={classes.gaView}>
                    {t(`GA.stay1`)}
                    {staySecText}
                  </div>
                )}
              </div>
            )}
          </>
        );
      default:
        return <div>{t(`preview.trigger.${type}`)}</div>;
    }
  }, [data]);

  return <div className={classes.root}>{content}</div>;
};

export default PreviewTrigger;
